import { useState } from "react";
import { Loader2, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { QUESTION_RENDERERS } from "@/components/forms/QuestionRenderers";

export function RecipientCard({ recipient }) {
  const rows = [["First name", recipient.first_name], ["Last name", recipient.last_name], ["Email", recipient.email], ["Mobile", recipient.mobile]].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="form-recipient">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-2">Your details</div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 border-b border-zinc-100 pb-1.5 sm:border-0 sm:pb-0">
            <dt className="text-zinc-500">{k}</dt>
            <dd className="font-medium text-[#0b2545] text-right truncate" data-testid={`form-recipient-${k.toLowerCase().replace(/\s/g, "-")}`}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function RecommendedProducts({ products, shopUrl }) {
  if (!products?.length) return null;
  return (
    <section className="space-y-3" data-testid="form-recommended">
      <h3 className="text-base md:text-lg font-semibold text-[#0b2545] flex items-center gap-2"><Sparkles size={16} className="text-[#b88a2b]" /> Recommended for you</h3>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {products.map((p) => (
          <li key={p.id} className="rounded-2xl border border-zinc-200 bg-white p-3 flex gap-3" data-testid={`form-product-${p.id}`}>
            {p.image_url && <img src={p.image_url} alt={p.name} className="h-20 w-20 rounded-xl object-contain bg-[#f6f4ef] shrink-0" loading="lazy" />}
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm text-[#0b2545] leading-snug">{p.name}</div>
              {p.price != null && <div className="text-sm text-zinc-700 mt-0.5">${Number(p.price).toFixed(2)}</div>}
              {p.matched?.length > 0 && <div className="text-[11px] text-[#b88a2b] mt-1">For: {p.matched.join(", ")}</div>}
              {p.slug && shopUrl && (
                <a href={`${shopUrl.replace(/\?.*$/, "")}?view=product&p=${encodeURIComponent(p.slug)}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0b2545] underline mt-1.5" data-testid={`form-product-link-${p.id}`}>
                  View product <ArrowRight size={12} />
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>
      {shopUrl && (
        <a href={shopUrl} className="inline-flex items-center justify-center w-full sm:w-auto rounded-full bg-[#b88a2b] px-6 py-3 text-sm font-bold text-white hover:opacity-90" data-testid="form-shop-cta">Shop the full collection</a>
      )}
    </section>
  );
}

export function ConfirmationPanel({ form, products, shopUrl, resultUrl }) {
  return (
    <div className="space-y-6" data-testid="form-confirmation">
      <div className="rounded-3xl bg-[#0b2545] text-white p-6 md:p-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#e2c27a] mb-2">Submitted</div>
        <h2 className="text-2xl md:text-3xl font-semibold" data-testid="form-confirmation-title">{form.confirmation_title}</h2>
        {form.confirmation_text && <p className="mt-3 text-sm md:text-base text-white/80 leading-relaxed" data-testid="form-confirmation-text">{form.confirmation_text}</p>}
        {resultUrl && (
          <a href={resultUrl} className="inline-flex items-center gap-2 mt-5 rounded-full bg-[#b88a2b] px-6 py-3 text-sm font-bold text-white hover:opacity-90" data-testid="form-result-link">
            <Sparkles size={15} /> Open my personalized protocol <ArrowRight size={15} />
          </a>
        )}
        {resultUrl && <p className="mt-3 text-xs text-white/60">We've also emailed it to you.</p>}
      </div>
      <RecommendedProducts products={products} shopUrl={shopUrl} />
    </div>
  );
}

// The engine: renders whatever `questions` the backend derived from the Form record.
export default function FormEngine({ data, onSubmit }) {
  const [answers, setAnswers] = useState(() => {
    // ?pick=<optionId>[,…] from an in-email option card → pre-select on the matching question
    const picks = new URLSearchParams(window.location.search).getAll("pick").flatMap((v) => v.split(",")).filter(Boolean);
    const base = { ...(data.answers || {}) };
    if (picks.length) {
      for (const q of data.questions || []) {
        const ids = q.options.map((o) => o.id).filter((id) => picks.includes(id));
        if (ids.length) base[q.key] = Array.from(new Set([...(base[q.key] || []), ...ids]));
      }
    }
    return base;
  });
  const [submitting, setSubmitting] = useState(false);
  const submitted = (data.status || "").toLowerCase() === "submitted";
  const missing = data.questions.filter((q) => q.required && q.options.length > 0 && !(answers[q.key] || []).length);

  async function submit() {
    if (missing.length) { toast.error("Please answer every question before submitting"); return; }
    setSubmitting(true);
    try { await onSubmit(answers); } finally { setSubmitting(false); }
  }

  if (submitted) return <ConfirmationPanel form={data.form} products={data.recommended_products} shopUrl={data.shop_url} resultUrl={data.result_url} />;

  return (
    <div className="space-y-8">
      <RecipientCard recipient={data.recipient} />
      {data.questions.map((q, i) => {
        const R = QUESTION_RENDERERS[q.type];
        return R ? <R key={q.key} question={q} index={i} value={answers[q.key] || []} onChange={(v) => setAnswers((a) => ({ ...a, [q.key]: v }))} /> : null;
      })}
      {data.questions.length === 0 && (
        <p className="rounded-xl border border-dashed border-zinc-300 bg-white/60 px-4 py-6 text-center text-sm text-zinc-500" data-testid="form-no-questions">This form has no questions configured yet.</p>
      )}
      <Button onClick={submit} disabled={submitting || data.questions.length === 0} className="w-full h-12 rounded-full bg-[#0b2545] hover:bg-[#143462] text-white text-base font-semibold" data-testid="form-submit">
        {submitting ? <Loader2 className="animate-spin" size={18} /> : <>{data.form.submit_text} <ArrowRight size={16} className="ml-1.5" /></>}
      </Button>
    </div>
  );
}
